/**
 * ============================================================================
 * FILE MANAGER COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Manage uploaded files with list, preview, and actions
 */

import React, { useState, useEffect } from 'react';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import FilePreview from './FilePreview';
import Tabs from '../common/Tabs';
import Select from '../common/Select';

/**
 * File Manager Component
 * @param {Object} props
 * @param {Array} props.files - Array of file objects
 * @param {Function} props.onFileDelete - Delete callback
 * @param {Function} props.onFileDownload - Download callback
 * @param {string} props.folder - Folder filter
 * @returns {JSX.Element}
 */
const FileManager = ({ files = [], onFileDelete, onFileDownload, folder }) => {
  const { deleteFile, downloadFile, loading } = useStorage();
  const { showSuccess, showError } = useToast();
  const { formatDate, language } = useI18n();

  const [activeTab, setActiveTab] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedFiles, setSelectedFiles] = useState([]);

  const getFileType = (file) => {
    const type = file.type || file.mimeType || '';
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type.includes('pdf')) return 'document';
    if (type.includes('word') || type.includes('document')) return 'document';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'document';
    return 'other';
  };

  const filteredFiles = files.filter((file) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'images') return getFileType(file) === 'image';
    if (activeTab === 'documents') return getFileType(file) === 'document';
    if (activeTab === 'videos') return getFileType(file) === 'video';
    return true;
  });

  const tabs = [
    {
      id: 'all',
      label: language === 'fr' ? 'Tout' : 'All',
      count: files.length,
    },
    {
      id: 'images',
      label: language === 'fr' ? 'Images' : 'Images',
      count: files.filter((f) => getFileType(f) === 'image').length,
    },
    {
      id: 'documents',
      label: language === 'fr' ? 'Documents' : 'Documents',
      count: files.filter((f) => getFileType(f) === 'document').length,
    },
    {
      id: 'videos',
      label: language === 'fr' ? 'Vidéos' : 'Videos',
      count: files.filter((f) => getFileType(f) === 'video').length,
    },
  ];

  const handleDelete = async (file) => {
    try {
      if (onFileDelete) {
        await onFileDelete(file);
      } else {
        await deleteFile(file.path || file.url);
      }
      showSuccess(
        language === 'fr' ? 'Fichier supprimé' : 'File deleted'
      );
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de suppression' : 'Failed to delete')
      );
    }
  };

  const handleDownload = async (file) => {
    try {
      if (onFileDownload) {
        await onFileDownload(file);
      } else {
        await downloadFile(file.path || file.url, file.name || file.fileName);
      }
      showSuccess(
        language === 'fr' ? 'Téléchargement démarré' : 'Download started'
      );
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec du téléchargement' : 'Download failed')
      );
    }
  };

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">📁</span>
            {language === 'fr' ? 'Gestionnaire de fichiers' : 'File Manager'}
          </h3>
          {files.length > 0 && (
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {files.length} {language === 'fr' ? 'fichier(s)' : 'file(s)'}
            </span>
          )}
        </div>

        {/* Tabs */}
        {files.length > 0 && (
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="pills" />
        )}

        {/* Files List */}
        {filteredFiles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFiles.map((file, index) => (
              <FilePreview
                key={file.id || file.url || index}
                fileUrl={file.url || file.path}
                fileName={file.name || file.fileName}
                fileType={file.type || file.mimeType}
                fileSize={file.size || file.fileSize}
                onDelete={() => handleDelete(file)}
                onDownload={() => handleDownload(file)}
                showActions
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="📁"
            title={language === 'fr' ? 'Aucun fichier' : 'No files'}
            description={
              language === 'fr'
                ? 'Aucun fichier dans cette catégorie'
                : 'No files in this category'
            }
          />
        )}
      </div>
    </Card>
  );
};

export default FileManager;
